import { ChangeDetectorRef, Component, ElementRef, HostBinding, inject, OnInit, ViewChild } from '@angular/core';
import { AuthenticationService } from '../../service/auth/authentication.service';
import { NotificationService } from '../../service/common/notification/notification.service';
import { RoutingService } from '../../service/common/routing/routing.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmDialogService } from '../../service/common/dialog/confirm-dialog.service';
import { Netzbetreiber } from '../../domain/model/netzbetreiber';
import { NetzbetreiberService } from '../../service/netzbetreiber/netzbetreiber.service';
import { Adresse } from '../../domain/model/adresse.model';
import { map, startWith, take } from 'rxjs/operators';
import { NgForm, UntypedFormControl } from '@angular/forms';
import { ProfileService } from '../../service/auth/profile.service';
import { TnbUser } from '../../service/auth/tnb-user';
import { combineLatest, lastValueFrom, Observable } from 'rxjs';
import { NetzbetreiberKontaktinfos } from '../../domain/model/netzbetreiber-kontaktinfos.model';
import { DienstleistungsModule } from '../../domain/model/dienstleistungsmodule.enum';
import { DienstleistungsmoduleService } from '../../service/dienstleistungsmodule/dienstleistungsmodule.service';
import { Kontaktdaten } from '../../domain/model/kontaktdaten';
import { KontaktdatenService } from '../../service/kontaktdaten/kontaktdaten.service';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { DienstleistungsArt } from '../../domain/model/dienstleistungsart.model';
import { DienstleistungsartService } from '../../service/dienstleistungsmodule/dienstleistungsart.service';
import { DienstleistungsModuleBeschreibung } from '../../domain/model/dienstleistungsmodulebeschreibung.model';
import { WestnetzSidenavService } from '../../service/common/westnetz-sidenav/westnetz-sidenav.service';

@Component({
    selector: 'redispatch-netzbetreiber-detail',
    templateUrl: './netzbetreiber-detail.component.html',
    styleUrls: [
        './netzbetreiber-detail.component.scss',
    ],
    standalone: false,
})
export class NetzbetreiberDetailComponent implements OnInit {

    public readonly EMAIL_PATTERN = '(?:[a-z0-9!#$%&\'*+/=?^_`{|}~-]+(?:\\.[a-z0-9!#$%&\'*+/=?^_`{|}~-]+)*|"' +
        '(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")' +
        '@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}' +
        '(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\\])';
    @ViewChild('netzbetreiberForm', { static: false }) public netzbetreiberForm: NgForm;
    @ViewChild('ansprechpartnerInput') ansprechpartnerInput: ElementRef<HTMLInputElement>;
    @ViewChild(MatAutocompleteTrigger) autoTrigger: MatAutocompleteTrigger;
    public netzbetreiber: Netzbetreiber;
    public openedInSideNav = false;
    public netzbetreiberType: string = null;
    public originalNetzbetreiber = Netzbetreiber.createEmptyNetzbetreiber();
    public stadtwerkeId: string;
    public createNewMode = false;
    public westnetzAdmin = false;
    public ansprechpartnerForm = new UntypedFormControl();
    public availableWestnetzKontaktdaten$: Observable<Kontaktdaten[]>;
    public availableDienstleistungsmodule$: Observable<DienstleistungsModuleBeschreibung[]>;
    public splittedDienstleistungsmodule$: Observable<{
        selected: DienstleistungsModuleBeschreibung[],
        unselected: DienstleistungsModuleBeschreibung[]
    }>;
    public dienstleistungsarten$: Observable<DienstleistungsArt[]>;
    public westnetzAnsprechpartnerReadonly = '';
    private routingService = inject(RoutingService);
    private readonly DEFAULT_TEILGEBIET_VALUE = 'Stammgebiet';
    private pgonChangedConfirmed = false;
    private authService = inject(AuthenticationService);
    private confirmDialogService = inject(ConfirmDialogService);
    private notificationService = inject(NotificationService);
    private activeRoute = inject(ActivatedRoute);
    private kontaktdatenService = inject(KontaktdatenService);
    private router = inject(Router);
    private dienstleistungsmoduleService = inject(DienstleistungsmoduleService);
    private dienstleistungsartService = inject(DienstleistungsartService);
    private netzbetreiberService = inject(NetzbetreiberService);
    private profileService = inject(ProfileService);
    private westnetzSidenavService = inject(WestnetzSidenavService);
    private changeDetectorRef = inject(ChangeDetectorRef);

    @HostBinding('class.wv-80') get wv80(): boolean {
        return this.openedInSideNav;
    }

    protected get isWestnetzUser(): boolean {
        return this.netzbetreiberType === 'WESTNETZ';
    }

    protected get isWestnetzAdmin(): boolean {
        return this.westnetzAdmin;
    }

    protected get isStadtwerkUser(): boolean {
        return this.netzbetreiberType === 'STADTWERK';
    }

    ngOnInit(): void {
        this.openedInSideNav = this.activeRoute.snapshot.queryParams['openedInSideNav'] === 'true';
        this.stadtwerkeId = this.activeRoute.snapshot.queryParams['stadtwerkeId'];
        this.createNewMode = this.activeRoute.snapshot.queryParams['createnew'] === 'true';

        this.dienstleistungsarten$ = this.dienstleistungsartService.listDienstleistungsarten();
        this.profileService.getProfile()
            .pipe(take(1))
            .subscribe((profile: TnbUser) => {
                this.netzbetreiberType = profile.netzbetreiberType;
                this.westnetzAdmin = this.authService.isWestnetzAdmin(profile);

                if (this.createNewMode) {
                    if (this.westnetzAdmin) {
                        this.netzbetreiber = Netzbetreiber.createEmptyNetzbetreiber();
                        this.netzbetreiber.teilgebiet = this.DEFAULT_TEILGEBIET_VALUE;
                        this.netzbetreiberService.getAvailableStadtwerkeId()
                            .pipe(take(1))
                            .subscribe(stadtwerkeId => {
                                this.netzbetreiber.stadtwerkeId = stadtwerkeId;
                                this.changeDetectorRef.markForCheck();
                            });
                    } else {
                        this.closeSideNav();
                    }
                } else {
                    this.reloadNetzbetreiber(this.stadtwerkeId);
                }

                this.changeDetectorRef.markForCheck();
                this.availableDienstleistungsmodule$ = this.dienstleistungsmoduleService.listDienstleistungsModuleBeschreibung();
                if (this.westnetzAdmin) {
                    this.setupAvailableWestnetzKontaktdaten();
                }
            });
    }

    protected isSaveButtonDisabled(): boolean {
        return !this.netzbetreiberForm || !this.netzbetreiberForm.form.valid || this.netzbetreiberForm.form.pristine;
    }

    protected createNetzbetreiber(): void {
        this.netzbetreiberService.createNetzbetreiber(this.createSendableNetzbetreiber())
            .pipe(take(1))
            .subscribe(
                _nb => {
                    this.notificationService.success('Der Netzbetreiber wurde erstellt');
                    this.netzbetreiberService.refreshRequested$.next();
                    this.closeSideNav();
                },
            );
    }

    protected saveBasisDaten(): void {
        this.netzbetreiberService.saveNetzbetreiberBasisdaten(this.createSendableNetzbetreiber())
            .pipe(take(1))
            .subscribe(() => {
                this.notificationService.success('Der Netzbetreiber wurde gespeichert');
                this.netzbetreiberService.refreshRequested$.next();
                this.closeSideNav();
            });
    }

    protected saveContactData(): void {
        this.netzbetreiberService.saveNetzbetreiberKontaktdaten(this.netzbetreiber)
            .pipe(take(1))
            .subscribe(() => {
                this.notificationService.success('Ihre Kontaktdaten wurden gespeichert');
                this.router.navigateByUrl(this.routingService.netzbetreiberDetailPath(this.stadtwerkeId, this.openedInSideNav), {
                    skipLocationChange: true,
                });
                this.resetForm();
            });
    }

    protected selectAnsprechpartner(event: MatAutocompleteSelectedEvent): void {
        this.netzbetreiber.westnetzKontaktdaten.push(event.option.value as Kontaktdaten);
        this.ansprechpartnerInput.nativeElement.value = '';
        this.ansprechpartnerForm.patchValue('');
        this.netzbetreiberForm.form.markAsDirty();
        requestAnimationFrame(() => {
            this.autoTrigger.openPanel();
        });
    }

    protected removeAnsprechpartnerFromNetzbetreiber(data: Kontaktdaten): void {
        const index = this.netzbetreiber.westnetzKontaktdaten.indexOf(data);
        if (index >= 0) {
            this.netzbetreiber.westnetzKontaktdaten.splice(index, 1);
        }
        this.ansprechpartnerInput.nativeElement.value = '';
        this.ansprechpartnerForm.patchValue('');
        this.ansprechpartnerForm.markAsDirty();
        this.netzbetreiberForm.form.markAsDirty();
    }

    protected isSameDienstleistungsart(dl1: DienstleistungsArt | null, dl2: DienstleistungsArt | null): boolean {
        return dl1?.id === dl2?.id;
    }

    protected isHighlighted(module: DienstleistungsModule): boolean {
        if (!this.netzbetreiber.dienstleistungsart) {
            return false;
        }
        return this.netzbetreiber.dienstleistungsart.dienstleistungsmodule.findIndex(value => value === module) !== -1;
    }

    protected containsModule(module: DienstleistungsModule): boolean {
        return this.netzbetreiber.dienstleistungsmodule.findIndex(value => value === module) !== -1;
    }

    protected moduleChanged(event: MatCheckboxChange, module: DienstleistungsModule): void {
        if (event.checked) {
            if (this.netzbetreiber.dienstleistungsmodule.findIndex(value => value === module) === -1) {
                this.netzbetreiber.dienstleistungsmodule.push(module);
            }
        } else {
            const idx = this.netzbetreiber.dienstleistungsmodule.findIndex(value => value === module);
            if (idx !== -1) {
                this.netzbetreiber.dienstleistungsmodule.splice(idx, 1);
            }
        }
        this.netzbetreiberForm.form.markAsDirty();
    }

    protected async deleteNetzbetreiber() {
        if (await this.confirmDialogService.confirm(
            'Sind Sie sich sicher, dass Sie diesen Netzbetreiber löschen möchten? ' +
            'Diese Aktion kann nicht rückgängig gemacht werden.',
            'Netzbetreiber löschen',
            'warn',
            'Ja, Netzbetreiber mit allen Daten löschen')) {
            await lastValueFrom(this.netzbetreiberService.deleteNetzbetreiber(this.netzbetreiber));
            this.notificationService.success('Ausgewählter Netzbetreiber wurde gelöscht.');
            this.netzbetreiberService.refreshRequested$.next();
            this.closeSideNav();
        }
    }

    protected resetForm() {
        this.pgonChangedConfirmed = false;
        this.reloadNetzbetreiber(this.stadtwerkeId);
        this.netzbetreiberForm.form.markAsPristine();
    }

    protected async netzbetreiberNummerChanged() {
        if (!this.createNewMode && !this.pgonChangedConfirmed) {
            if (await this.confirmDialogService.confirm(
                'Sind Sie sich sicher, dass Sie die Netzbetreibernummer (PGON) ändern/löschen möchten? ' +
                'Es kann sein, dass ein oder mehrere Benutzer ihren Zugriff verlieren.',
                'Achtung',
                'warn',
                'Ja, Netzbetreibernummer (PGON) ändern',
                'Abbrechen',
            )) {
                this.pgonChangedConfirmed = true;
            } else {
                this.netzbetreiber.netzbetreibernummer = this.originalNetzbetreiber.netzbetreibernummer;
            }
        }
    }

    protected closeSideNav(): void {
        this.westnetzSidenavService.closeRightnav();
        this.router.navigate([
            this.routingService.netzbetreiberPath,
        ], {
            queryParams: { stadtwerkeId: null, openedInSideNav: null, createnew: null },
            queryParamsHandling: 'merge',
        });
    }

    private reloadNetzbetreiber(stadtwerkeId: string): void {
        this.netzbetreiberService.getNetzbetreiber(stadtwerkeId)
            .pipe(take(1))
            .subscribe(nb => {
                this.netzbetreiber = nb;
                this.westnetzAnsprechpartnerReadonly = (this.netzbetreiber.westnetzKontaktdaten.length > 0) ?
                    this.netzbetreiber.westnetzKontaktdaten.map((kb) => kb.name).join('/') :
                    'Kein Ansprechpartner zugewiesen';

                this.splittedDienstleistungsmodule$ = this.dienstleistungsmoduleService.listDienstleistungsModuleBeschreibung().pipe(
                    map((modules) => {
                        return modules.reduce((value, module) => {
                            if (this.containsModule(module.dienstleistungsmodule)) {
                                value.selected.push(module);
                            } else {
                                value.unselected.push(module);
                            }
                            return value;
                        }, { selected: [], unselected: [] });
                    }),
                );
                this.originalNetzbetreiber = JSON.parse(JSON.stringify(this.netzbetreiber));
                if (!this.netzbetreiber.adresse) {
                    this.netzbetreiber.adresse = new Adresse();
                }
                if (!this.netzbetreiber.kontaktInfos) {
                    this.netzbetreiber.kontaktInfos = new NetzbetreiberKontaktinfos();
                }
                this.changeDetectorRef.markForCheck();
            });
    }

    // removes objects that would render the NB invalid at the backend
    private createSendableNetzbetreiber(): Netzbetreiber {
        const netzbetreiberToBeSent = JSON.parse(JSON.stringify(this.netzbetreiber));
        delete netzbetreiberToBeSent.kontaktInfos;
        delete netzbetreiberToBeSent.adresse;
        return netzbetreiberToBeSent;
    }

    private setupAvailableWestnetzKontaktdaten(): void {
        this.availableWestnetzKontaktdaten$ = combineLatest(
            [
                this.kontaktdatenService.listKontaktdaten(),
                this.ansprechpartnerForm.valueChanges.pipe(startWith('')),
            ],
        ).pipe(
            map(
                (
                    [
                        initialKontaktdaten,
                        searchValue,
                    ],
                ) => {
                    if (typeof searchValue !== 'string') {
                        return initialKontaktdaten;
                    }
                    return initialKontaktdaten.filter(value => {
                        return value.name.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1;
                    });
                }),
            map((kontaktdaten: Kontaktdaten[]) => {
                return kontaktdaten.filter(value => {
                    return this.netzbetreiber.westnetzKontaktdaten.findIndex(alreadyAssigned => {
                        return alreadyAssigned.id === value.id;
                    }) === -1;
                });
            }),
        );
    }
}
